/**
 * Fullscreen Manager - Provides automatic fullscreen for mobile landscape mode
 * across all pages of the application
 */

export class FullscreenManager {
    constructor() {
        this.isFullscreenMode = false;
        this.isInitialized = false;
    }

    /**
     * Check if device is in mobile landscape orientation
     */
    isMobileLandscape() {
        return window.innerWidth > window.innerHeight &&
               window.innerWidth <= 1024 &&
               ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    }

    /**
     * Check if fullscreen API is supported
     */
    isFullscreenSupported() {
        return document.documentElement.requestFullscreen ||
               document.documentElement.webkitRequestFullscreen ||
               document.documentElement.msRequestFullscreen;
    }

    /**
     * Enter fullscreen mode
     */
    async enterFullscreen() {
        if (!this.isFullscreenSupported()) {
            console.warn('Fullscreen API not supported on this browser');
            return false;
        }

        try {
            if (document.documentElement.requestFullscreen) {
                await document.documentElement.requestFullscreen();
            } else if (document.documentElement.webkitRequestFullscreen) {
                await document.documentElement.webkitRequestFullscreen();
            } else if (document.documentElement.msRequestFullscreen) {
                await document.documentElement.msRequestFullscreen();
            }

            // Wait a bit for fullscreen to take effect
            await new Promise(resolve => setTimeout(resolve, 100));

            // Check if fullscreen actually worked
            const isActuallyFullscreen = !!(
                document.fullscreenElement ||
                document['webkitFullscreenElement'] ||
                document['msFullscreenElement']
            );

            if (isActuallyFullscreen) {
                this.isFullscreenMode = true;
                document.body.classList.add('fullscreen-mode');
                console.log('✅ Successfully entered fullscreen mode - tab bar hidden');
                return true;
            } else {
                console.warn('⚠️ Fullscreen API called but fullscreen not detected');
                return false;
            }
        } catch (error) {
            console.warn('❌ Failed to enter fullscreen:', error);
            return false;
        }
    }

    /**
     * Exit fullscreen mode
     */
    async exitFullscreen() {
        try {
            if (document.exitFullscreen) {
                await document.exitFullscreen();
            } else if (document['webkitExitFullscreen']) {
                await document['webkitExitFullscreen']();
            } else if (document['msExitFullscreen']) {
                await document['msExitFullscreen']();
            }

            this.isFullscreenMode = false;
            document.body.classList.remove('fullscreen-mode');
            console.log('Exited fullscreen mode');
            return true;
        } catch (error) {
            console.warn('Failed to exit fullscreen:', error);
            return false;
        }
    }

    /**
     * Toggle fullscreen mode
     */
    async toggleFullscreen() {
        if (this.isFullscreenMode) {
            return await this.exitFullscreen();
        } else {
            return await this.enterFullscreen();
        }
    }

    /**
     * Initialize fullscreen manager for a page
     */
    init(options = {}) {
        if (this.isInitialized) {
            console.warn('FullscreenManager already initialized');
            return;
        }

        const {
            enableDoubleTap = true,
            enableAutoLandscape = true,
            hideNavigationInFullscreen = false,
            customFullscreenClass = 'fullscreen-mode'
        } = options;

        // Override fullscreen class if specified
        this.fullscreenClass = customFullscreenClass;

        // Listen for orientation changes
        if (enableAutoLandscape) {
            window.addEventListener('orientationchange', () => {
                setTimeout(() => {
                    if (this.isMobileLandscape() && !this.isFullscreenMode) {
                        console.log('📱 Mobile landscape detected, entering fullscreen');
                        setTimeout(() => {
                            if (this.isMobileLandscape() && !this.isFullscreenMode) {
                                this.enterFullscreen();
                            }
                        }, 500);
                    } else if (!this.isMobileLandscape() && this.isFullscreenMode) {
                        console.log('📱 No longer in mobile landscape, exiting fullscreen');
                        this.exitFullscreen();
                    }
                }, 100);
            });
        }

        // Listen for fullscreen changes
        document.addEventListener('fullscreenchange', () => {
            this.isFullscreenMode = !!(
                document.fullscreenElement ||
                document['webkitFullscreenElement'] ||
                document['msFullscreenElement']
            );
            document.body.classList.toggle(this.fullscreenClass, this.isFullscreenMode);

            // Handle navigation hiding if enabled
            if (hideNavigationInFullscreen) {
                const navElements = document.querySelectorAll('nav, .navigation, #navigation-container, .nav-container');
                navElements.forEach(nav => {
                    if (nav) {
                        nav.style.display = this.isFullscreenMode ? 'none' : '';
                    }
                });
            }

        });

        // Add double-tap to toggle fullscreen on mobile
        if (enableDoubleTap) {
            let lastTap = 0;
            document.addEventListener('touchend', (e) => {
                const currentTime = new Date().getTime();
                const tapLength = currentTime - lastTap;

                if (tapLength < 500 && tapLength > 0 && this.isMobileLandscape()) {
                    e.preventDefault();
                    this.toggleFullscreen();
                    console.log('👆 Double-tap fullscreen toggle');
                }
                lastTap = currentTime;
            });
        }

        // Initial check
        if (this.isMobileLandscape()) {
            setTimeout(() => {
                this.enterFullscreen();
            }, 1000); // Delay to ensure page is fully loaded
        }

        this.isInitialized = true;
        console.log('📱 FullscreenManager initialized');
    }

    /**
     * Cleanup fullscreen manager
     */
    destroy() {
        this.exitFullscreen();
        this.isInitialized = false;
    }
}

// Export singleton instance
export const fullscreenManager = new FullscreenManager();

// Make globally available for inline scripts
if (typeof window !== 'undefined') {
    window.fullscreenManager = fullscreenManager;
}
